import React, { CSSProperties, FormEvent, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { AlertCircle, Eye, EyeOff, Lock, Star } from "lucide-react"
import { MilitaryLoginLoader } from "../../components/MilitaryLoginLoader"

/* ─── paleta ─────────────────────────────────────────────────────────── */
const G = {
  bg:        "#050d07",
  card:      "rgba(8,18,10,0.92)",
  border:    "rgba(74,180,100,0.22)",
  borderHi:  "rgba(74,180,100,0.55)",
  green:     "rgba(74,180,100,0.9)",
  greenDim:  "rgba(74,180,100,0.45)",
  greenFaint:"rgba(74,180,100,0.12)",
  text:      "rgba(200,230,210,0.95)",
  textMuted: "rgba(74,180,100,0.55)",
  red:       "rgba(220,90,70,0.85)",
  redBg:     "rgba(200,60,40,0.08)",
  redBorder: "rgba(200,60,40,0.28)",
  mono:      "'Geist Mono','Courier New',monospace",
  sans:      "'Geist',system-ui,sans-serif",
}

/* inline-style helpers */
const s = {
  root: {
    position:"relative", minHeight:"100vh", display:"flex",
    alignItems:"center", justifyContent:"center", padding:"1.5rem",
    background: G.bg, overflow:"hidden", fontFamily: G.sans,
  } as CSSProperties,

  gridBg: {
    position:"absolute", inset:0, zIndex:0,
    backgroundImage: `radial-gradient(circle,${G.greenFaint} 1px,transparent 1px)`,
    backgroundSize: "38px 38px",
  } as CSSProperties,

  glow: {
    position:"absolute", inset:0, zIndex:0,
    background:`radial-gradient(ellipse 60% 50% at 50% 50%,rgba(30,80,40,0.35) 0%,transparent 70%)`,
    animation:"signGlowPulse 6s ease-in-out infinite",
  } as CSSProperties,

  scan: {
    position:"absolute", left:0, right:0, height:"1px", zIndex:1,
    background:`linear-gradient(90deg,transparent,${G.greenDim},transparent)`,
    animation:"signScan 9s linear infinite",
  } as CSSProperties,

  card: {
    position:"relative", zIndex:2, width:"100%", maxWidth:"420px",
    background: G.card,
    border:`1px solid ${G.border}`,
    backdropFilter:"blur(12px)",
    boxShadow:`0 0 0 1px rgba(74,180,100,0.04),0 24px 64px rgba(0,0,0,0.65),0 0 80px rgba(30,100,50,0.08)`,
    animation:"signCardIn 0.45s cubic-bezier(0.16,1,0.3,1)",
  } as CSSProperties,

  corner: (pos:"tl"|"tr"|"bl"|"br"): CSSProperties => {
    const top    = pos.startsWith("t") ? "-1px" : undefined
    const bottom = pos.startsWith("b") ? "-1px" : undefined
    const left   = pos.endsWith("l")  ? "-1px" : undefined
    const right  = pos.endsWith("r")  ? "-1px" : undefined
    return {
      position:"absolute", width:"18px", height:"18px",
      borderTop:    (pos==="tl"||pos==="tr") ? `2px solid ${G.borderHi}` : undefined,
      borderBottom: (pos==="bl"||pos==="br") ? `2px solid ${G.borderHi}` : undefined,
      borderLeft:   (pos==="tl"||pos==="bl") ? `2px solid ${G.borderHi}` : undefined,
      borderRight:  (pos==="tr"||pos==="br") ? `2px solid ${G.borderHi}` : undefined,
      top, bottom, left, right,
    }
  },

  topBar: {
    display:"flex", alignItems:"center", justifyContent:"space-between",
    padding:"0.45rem 1.25rem",
    borderBottom:`1px solid ${G.greenFaint}`,
    background:"rgba(74,180,100,0.03)",
  } as CSSProperties,

  classifiedBadge: {
    fontFamily:G.mono, fontSize:"10px", letterSpacing:"3px",
    color:G.textMuted, textTransform:"uppercase",
  } as CSSProperties,

  version: {
    fontFamily:G.mono, fontSize:"10px", letterSpacing:"2px",
    color:"rgba(74,180,100,0.3)",
  } as CSSProperties,

  header: {
    display:"flex", flexDirection:"column", alignItems:"center",
    gap:"0.45rem", padding:"1.75rem 1.5rem 1.25rem",
  } as CSSProperties,

  badgeRing: {
    width:"72px", height:"72px", borderRadius:"50%",
    border:`2px solid rgba(74,180,100,0.4)`,
    background:"rgba(30,80,40,0.12)",
    display:"flex", alignItems:"center", justifyContent:"center",
    overflow:"hidden",
    boxShadow:"0 0 24px rgba(74,180,100,0.12),inset 0 0 16px rgba(74,180,100,0.05)",
    animation:"signShieldPulse 3s ease-in-out infinite",
  } as CSSProperties,

  stars: {
    display:"flex", gap:"3px", marginTop:"4px",
    color:"rgba(74,180,100,0.6)",
  } as CSSProperties,

  title: {
    fontFamily:G.sans, fontSize:"1.05rem", fontWeight:700,
    letterSpacing:"3px", textTransform:"uppercase",
    color:"rgba(180,230,195,0.95)", textAlign:"center", margin:0,
    marginTop:"0.5rem",
  } as CSSProperties,

  subtitle: {
    fontFamily:G.mono, fontSize:"11px", letterSpacing:"1.5px",
    color:G.textMuted, textTransform:"uppercase", margin:0,
  } as CSSProperties,

  restricted: {
    display:"flex", alignItems:"center", gap:"5px",
    marginTop:"0.2rem", padding:"3px 10px",
    border:"1px solid rgba(200,100,60,0.3)",
    background:"rgba(200,80,50,0.05)",
    fontFamily:G.mono, fontSize:"9px", letterSpacing:"1.5px",
    color:"rgba(220,130,100,0.8)", textTransform:"uppercase",
  } as CSSProperties,

  divider: {
    display:"flex", alignItems:"center", gap:"8px",
    padding:"0 1.5rem", margin:"0 0 0.25rem",
  } as CSSProperties,

  dividerLine: {
    flex:1, height:"1px",
    background:`linear-gradient(90deg,transparent,rgba(74,180,100,0.2),transparent)`,
  } as CSSProperties,

  dividerDot: {
    width:"4px", height:"4px", borderRadius:"50%",
    background:"rgba(74,180,100,0.45)", flexShrink:0,
  } as CSSProperties,

  form: {
    display:"flex", flexDirection:"column",
    gap:"1rem", padding:"1rem 1.5rem 0",
  } as CSSProperties,

  error: {
    display:"flex", alignItems:"center", gap:"8px",
    padding:"0.6rem 0.875rem",
    background:G.redBg,
    border:`1px solid ${G.redBorder}`,
    borderLeft:`3px solid rgba(200,80,60,0.7)`,
    fontFamily:G.mono, fontSize:"12px", color:G.red,
    animation:"signFadeIn 0.25s ease-out",
  } as CSSProperties,

  field: {
    display:"flex", flexDirection:"column", gap:"5px",
  } as CSSProperties,

  label: {
    fontFamily:G.mono, fontSize:"10px", letterSpacing:"2px",
    textTransform:"uppercase", color:G.textMuted,
  } as CSSProperties,

  input: {
    width:"100%", height:"42px", padding:"0 0.875rem",
    background:"rgba(20,40,25,0.6)",
    border:`1px solid ${G.border}`,
    color:G.text, fontFamily:G.mono, fontSize:"14px",
    letterSpacing:"0.4px", outline:"none",
    transition:"border-color 0.2s,box-shadow 0.2s,background 0.2s",
  } as CSSProperties,

  inputWrap: { position:"relative" } as CSSProperties,

  eyeBtn: {
    position:"absolute", right:"0.75rem", top:"50%",
    transform:"translateY(-50%)",
    background:"transparent", border:"none", cursor:"pointer",
    color:G.greenDim, display:"flex", alignItems:"center",
    padding:0, transition:"color 0.2s",
  } as CSSProperties,

  submit: {
    width:"100%", height:"44px", marginTop:"0.25rem",
    background:"rgba(35,90,50,0.3)",
    border:`1px solid rgba(74,180,100,0.42)`,
    color:"rgba(160,230,180,0.95)",
    fontFamily:G.mono, fontSize:"12px", fontWeight:600,
    letterSpacing:"3px", textTransform:"uppercase",
    cursor:"pointer",
    transition:"background 0.2s,box-shadow 0.2s,border-color 0.2s",
  } as CSSProperties,

  footer: {
    display:"flex", alignItems:"center", justifyContent:"center",
    gap:"10px", padding:"1.25rem 1.5rem 0", marginTop:"1rem",
    borderTop:`1px solid rgba(74,180,100,0.07)`,
    fontFamily:G.mono, fontSize:"9px", letterSpacing:"1.5px",
    color:"rgba(74,180,100,0.28)", textTransform:"uppercase",
  } as CSSProperties,
}

export function SignInOficiais() {
  const [nickname,     setNickname]     = useState("")
  const [password,     setPassword]     = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading,    setIsLoading]    = useState(false)
  const [error,        setError]        = useState("")
  const [inputFocus,   setInputFocus]   = useState<string|null>(null)

  const { signIn } = useAuth()
  const navigate    = useNavigate()

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError("")
    if (!nickname.trim()) { setError("Identificação militar obrigatória"); return }
    if (!password.trim()) { setError("Senha de acesso obrigatória");        return }
    setIsLoading(true)
    const minDelay = new Promise<void>(res => setTimeout(res, 3600))
    try {
      const [errorResult] = await Promise.all([
        signIn({ nick: nickname, senha: password }),
        minDelay,
      ])
      if (errorResult) {
        setError("Credenciais inválidas ou acesso não autorizado")
        setIsLoading(false)
        return
      }
      navigate("/dashboard")
    } catch {
      setError("Falha na comunicação com o servidor")
      setIsLoading(false)
    }
  }

  const focusedInput = (id: string): CSSProperties => inputFocus === id ? {
    borderColor:"rgba(74,180,100,0.55)",
    background:"rgba(25,50,30,0.7)",
    boxShadow:"0 0 0 3px rgba(74,180,100,0.08),inset 0 0 8px rgba(74,180,100,0.04)",
  } : {}

  return (
    <>
      <style>{`
        @keyframes signGlowPulse { 0%,100%{opacity:.8} 50%{opacity:1} }
        @keyframes signScan      { 0%{top:-2px} 100%{top:100vh} }
        @keyframes signCardIn    { from{opacity:0;transform:translateY(18px) scale(.98)} to{opacity:1;transform:none} }
        @keyframes signShieldPulse {
          0%,100%{box-shadow:0 0 20px rgba(74,180,100,.12),inset 0 0 16px rgba(74,180,100,.05)}
          50%    {box-shadow:0 0 32px rgba(74,180,100,.28),inset 0 0 24px rgba(74,180,100,.10)}
        }
        @keyframes signFadeIn { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:none} }
        .sig-submit:hover:not(:disabled){
          background:rgba(40,110,60,.55)!important;
          border-color:rgba(74,180,100,.7)!important;
          box-shadow:0 0 20px rgba(74,180,100,.12)!important;
        }
        .sig-submit:active:not(:disabled){ transform:translateY(1px); }
        .sig-submit:disabled{ opacity:.45; cursor:not-allowed; }
        .sig-eye-btn:hover{ color:rgba(74,180,100,.85)!important; }
        input::placeholder{ color:rgba(74,180,100,.22)!important; font-size:13px; }
      `}</style>

      <MilitaryLoginLoader visible={isLoading} />

      <div style={s.root}>
        <div style={s.gridBg} />
        <div style={s.glow}   />
        <div style={s.scan}   />

        <div style={s.card}>
          <span style={s.corner("tl")} />
          <span style={s.corner("tr")} />
          <span style={s.corner("bl")} />
          <span style={s.corner("br")} />

          {/* top bar */}
          <div style={s.topBar}>
            <span style={s.classifiedBadge}>⬡ CLASSIFICADO</span>
            <span style={s.version}>v2.5.1</span>
          </div>

          {/* header */}
          <div style={s.header}>
            <div>
              <div style={s.badgeRing}>
                <img
                  src={`https://www.habbo.com.br/habbo-imaging/avatarimage?img_format=png&user=${encodeURIComponent(nickname.trim() || 'Rainer')}&direction=3&head_direction=3&gesture=sml&headonly=1&size=l`}
                  alt="Avatar"
                  style={{ width:"72px", height:"72px", objectFit:"cover" }}
                />
              </div>
              <div style={s.stars}>
                {[0,1,2,3,4].map(i => <Star key={i} size={8} fill="currentColor" />)}
              </div>
            </div>
            <h1 style={s.title}>EXBR — PAINEL</h1>
            <p style={s.subtitle}>Sistema de Gestão Militar</p>
            <div style={s.restricted}>
              <Lock size={10} />
              <span>ACESSO RESTRITO A PESSOAL AUTORIZADO</span>
            </div>
          </div>

          {/* divisor */}
          <div style={s.divider}>
            <span style={s.dividerLine} />
            <span style={s.dividerDot} />
            <span style={s.dividerLine} />
          </div>

          {/* form */}
          <form onSubmit={handleSubmit} style={s.form}>
            {error && (
              <div style={s.error}>
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}

            <div style={s.field}>
              <label htmlFor="nickname" style={s.label}>Identificação Militar</label>
              <input
                id="nickname"
                type="text"
                autoComplete="username"
                placeholder="Digite sua identificação"
                value={nickname}
                onChange={e => setNickname(e.target.value.replace(/\s/g, ""))}
                onKeyDown={e => e.key === " " && e.preventDefault()}
                onFocus={() => setInputFocus("nickname")}
                onBlur={() => setInputFocus(null)}
                style={{ ...s.input, ...focusedInput("nickname") }}
                required
              />
            </div>

            <div style={s.field}>
              <label htmlFor="password" style={s.label}>Senha de Acesso</label>
              <div style={s.inputWrap}>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onFocus={() => setInputFocus("password")}
                  onBlur={() => setInputFocus(null)}
                  style={{ ...s.input, paddingRight:"2.75rem", ...focusedInput("password") }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  style={s.eyeBtn}
                  className="sig-eye-btn"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              style={s.submit}
              className="sig-submit"
              disabled={isLoading}
            >
              {isLoading ? "AUTENTICANDO..." : "▶  ACESSAR SISTEMA"}
            </button>
          </form>

          {/* footer */}
          <div style={s.footer}>
            <span>CLASSIFIED</span>
            <span style={{ color:"rgba(74,180,100,0.18)" }}>·</span>
            <span>NÍVEL: RESTRITO</span>
            <span style={{ color:"rgba(74,180,100,0.18)" }}>·</span>
            <span>© 2026 EXBR</span>
          </div>
        </div>
      </div>
    </>
  )
}
